import Decimal from 'decimal.js'

import { isPositiveDecimal, isSignedDecimal } from '@/features/hedge/lib/hedge-bands'
import { SpreaderLegInput } from '@/features/spreader/types'
import { SpreaderLegRole } from '@/types/generated/algo'

function isFractionInRange(value: string): boolean {
  if (!isSignedDecimal(value)) return false
  const d = new Decimal(value)
  return d.greaterThan(0) && d.lessThanOrEqualTo(1)
}

export type SpreaderValidationError =
  | 'exactlyOneQuoting'
  | 'atLeastOneTaking'
  | 'positiveQuantity'
  | 'sliceRange'
  | 'positiveLimitPrice'

// Mirrors the server-side refusals from the Spreader integration guide §3.2/§9. Account/
// symbol resolution is already guaranteed by getSubmissionRows() (a row that doesn't resolve
// to a valid active account never reaches here), so this only checks what that step can't:
// role assignment, slice size, and limit price.
export function validateSpreaderLegs(
  legs: SpreaderLegInput[],
  slicePct: string,
  limitPrice: string
): SpreaderValidationError | null {
  const quotingLegs = legs.filter((l) => l.role === SpreaderLegRole.SPREADER_LEG_ROLE_QUOTING)
  const takingLegs = legs.filter((l) => l.role === SpreaderLegRole.SPREADER_LEG_ROLE_TAKING)

  if (quotingLegs.length !== 1) return 'exactlyOneQuoting'
  if (takingLegs.length < 1) return 'atLeastOneTaking'
  if (legs.some((l) => !isPositiveDecimal(l.quantity))) return 'positiveQuantity'
  if (!isFractionInRange(slicePct)) return 'sliceRange'
  if (limitPrice.trim() && !isPositiveDecimal(limitPrice)) {
    return 'positiveLimitPrice'
  }
  return null
}
